/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import styled, { css } from "styled-components";
import { Button, FlexContainer, theme, Tooltip } from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { useCustomers } from "~/services/customers";

type Props = {
  value: Provider[];
  onSelect: (provider: Provider) => void;
};

export function ProviderMultiSelect({ value, onSelect: onChange }: Props) {
  const { isAdminLite } = useCustomers();

  const providers = useMemo(
    () =>
      PROVIDERS_CONFIG.filter((provider) =>
        isAdminLite ? provider.showForLite : true
      ),
    [isAdminLite]
  );

  return (
    <FlexContainer justifyContent="flex-start">
      {providers.map((provider) => (
        <Tooltip key={provider.caption} caption={provider.caption}>
          <ProviderButton
            icon={provider.icon}
            severity="medium"
            value={value?.find(
              (providerValue) => providerValue === provider.value
            )}
            onClick={() => onChange(provider.value)}
          />
        </Tooltip>
      ))}
    </FlexContainer>
  );
}

const PROVIDERS_CONFIG = [
  {
    caption: "AWS",
    icon: "aws",
    value: Provider.Aws,
    showForLite: true,
  },
  {
    caption: "Google Cloud",
    icon: "gcp",
    value: Provider.Gcp,
    showForLite: true,
  },
  {
    caption: "AZURE",
    icon: "azure",
    value: Provider.Azure,
    showForLite: true,
  },
  {
    caption: "IBMCLOUD",
    icon: "ibmCloud",
    value: Provider.Ibmcloud,
  },
  {
    caption: "VMWARE",
    icon: "vmware",
    value: Provider.Vmware,
  },
  {
    caption: "Kubernetes",
    icon: "kubernetes",
    value: Provider.Kubernetes,
  },
  {
    caption: "Openshift",
    icon: "openshift",
    value: Provider.Openshift,
  },
];

const ProviderButton = styled(Button)`
  border-color: ${theme.color.border.input};
  filter: grayscale(100%);

  ${({ value: selected }) =>
    selected &&
    css`
      border-color: ${theme.color.border.focus};
      filter: none;
    `}

  transition: ${theme.transition};
  margin-right: ${theme.spacing.spacing02};
`;
